// import { mockDeals } from "./__mocks__/deals";
import { generateDeals } from './__mocks__/dynamicDeals';

import { AFFILIATE_DEALS_PROXY_URL } from '../config';
import { withTokensIfNeeded } from '../utils/withTokensIfNeeded';
import { getLinkIdFromAffiliateUrl } from './getLinkIdFromAffiliateUrl';
import { mapDealsToUIObjects } from './maps';
import { DealAPIResponse, DealUIObject } from './types';

export async function getDealsFromAffiliateUrl(
    affiliateUrl: string,
    token?: string
): Promise<DealUIObject[]> {
    const linkId = getLinkIdFromAffiliateUrl(affiliateUrl);

    const deals = await getDealsByLinkId(linkId);
    deals.sort((a: DealUIObject, b: DealUIObject) => b.sortScore - a.sortScore);

    if (!token) {
        return deals;
    }

    return deals.map((deal) => {
        return {
            ...deal,
            url:
                typeof deal.url === 'string'
                    ? deal.url.replace('{{token}}', token).replace('{{web}}', 'true')
                    : deal.url,
        };
    });
}

export async function getDealsByLinkId(linkId: string): Promise<DealUIObject[]> {
    const response = await withTokensIfNeeded<DealAPIResponse>(
        `${AFFILIATE_DEALS_PROXY_URL}&filters[link_id]=${linkId}&filters[ends_at_gt]=${Math.floor(
            Date.now() / 1000
        )}`,
        {
            method: 'GET',
            mode: 'cors',
        }
    );

    const deals = response.data?.data || [];
    return mapDealsToUIObjects(deals);

    /* enable this instead if you want mock data: */
    // const mockDeals = generateDeals(123);
    // return mapDealsToUIObjects(mockDeals);
}

export const getDeals = async (limit = 25): Promise<DealUIObject[]> => {
    const defaultFilter = `filters[ends_at_gt]=${Math.floor(Date.now() / 1000)}`;

    const response = await withTokensIfNeeded<DealAPIResponse>(
        `${AFFILIATE_DEALS_PROXY_URL}&page[limit]=${limit}&order[rank]=DESC&${defaultFilter}`,
        {
            headers: {
                ContentType: 'application/json',
            },
        }
    );

    if (response.data?.data) {
        return mapDealsToUIObjects(response.data.data);
    }
    return [];
};

export const getAllDeals = async (): Promise<DealUIObject[]> => {
    const defaultFilter = `filters[ends_at_gt]=${Math.floor(Date.now() / 1000)}`;
    const pageLimit = 100;
    let offset = 0;
    let dealsToReturn: DealUIObject[] = [];

    try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const response = await withTokensIfNeeded<DealAPIResponse>(
                `${AFFILIATE_DEALS_PROXY_URL}&page[offset]=${offset}&page[limit]=${pageLimit}&order[rank]=DESC&${defaultFilter}`,
                {
                    headers: {
                        ContentType: 'application/json',
                    },
                }
            );

            if (response.data?.data) {
                dealsToReturn = [...dealsToReturn, ...mapDealsToUIObjects(response.data.data)];
            }

            if (!response.data?.data.length) {
                break;
            }

            offset += pageLimit;
        }
    } catch (error) {
        console.error('Error fetching all deals:', error);
        throw new Error('Failed to fetch all deals');
    }

    const cache = {} as Record<number, DealUIObject>;
    dealsToReturn.forEach((element: DealUIObject) => {
        if (!cache[element.id]) {
            cache[element.id] = element;
        }
    });
    return Object.values(cache);
};
